import { AvailabilityType, Product, ProductType, Unit } from '@thro-types/leihbar';
import { User } from '@thro-types/users';

import { TFunction } from 'i18next';
import { pick } from 'lodash';
import * as yup from 'yup';

import { translations } from 'locales/i18n';
import { phoneRegExp } from 'utils/regex';

export const productValidationSchema = (t: TFunction<'translation', undefined>) =>
  yup.object().shape({
    name: yup
      .string()
      .required(t(translations.validation.required()))
      .label(t(translations.pages.leihbar.product.create.productname())),
    description: yup
      .string()
      .required(t(translations.validation.required()))
      .label(t(translations.pages.leihbar.product.create.description())),
    categoryIds: yup
      .array()
      .of(
        yup
          .number()
          .required(t(translations.validation.required()))
          .label(t(translations.pages.leihbar.product.create.category()))
          .typeError(t(translations.validation.required()))
      )
      .min(1, t(translations.validation.minOne()))
      .required(t(translations.validation.required())),
    location: yup
      .object()
      .shape({ id: yup.number(), name: yup.string() })
      .required(t(translations.validation.required()))
      .label(t(translations.pages.leihbar.product.create.location()))
      .typeError(t(translations.validation.required())),
    image: yup
      .mixed()
      .required(t(translations.validation.productImage()))
      .label(t(translations.pages.leihbar.product.create.image())),
    lenderGroupId: yup
      .number()
      .required(t(translations.validation.required()))
      .label(t(translations.pages.leihbar.product.create.domain()))
      .typeError(t(translations.validation.required())),
    availabilityType: yup
      .string()
      .oneOf(Object.values(AvailabilityType))
      .required()
      .label(t(translations.pages.leihbar.product.create.alwaysAvailable())),
    possibleBookingSlots: yup.array().of(
      yup
        .object()
        .shape({
          start: yup.date().required(t(translations.validation.required())),
          end: yup.date().required(t(translations.validation.required()))
        })
        .required(t(translations.validation.required()))
    ),
    // recurringInterval: yup.object().shape({   //TODO make optional if not availabilityType
    //   weekdays: yup.array().of(yup.string()).required(),
    //   start: yup.number(),
    //   end: yup.number()
    // }),
    maxBooking: yup
      .object()
      .shape({
        value: yup.number().required(t(translations.validation.required())),
        unit: yup.string().required()
      })
      .required(),
    preparationTime: yup
      .object()
      .shape({
        value: yup.number().required(t(translations.validation.required())),
        unit: yup.string().required()
      })
      .required(),
    reactionTime: yup
      .object()
      .shape({
        unit: yup.string()
      })
      .default({ value: 0, unit: '' })
      .nullable()
      .optional(),
    hint: yup.string(),
    internalNote: yup.string(),
    visibilityGroupIds: yup.array().of(yup.number()),
    targetUserIds: yup.array().of(yup.number()),
    productType: yup.mixed<ProductType>().required(t(translations.validation.required())),
    lender: yup
      .object()
      .shape({
        email: yup
          .string()
          .email(t(translations.validation.email()))
          .required(t(translations.validation.required()))
          .label(t(translations.pages.leihbar.product.create.lender.email())),
        phone: yup
          .string()
          .matches(phoneRegExp, t(translations.validation.number()))
          .label(t(translations.pages.leihbar.product.create.lender.number()))
          .nullable()
      })
      .required()
  });

export const initialProductValues = (initialProduct: Product | undefined, currentUser: User) => {
  return initialProduct
    ? {
        ...pick(initialProduct, [
          'id',
          'name',
          'description',
          'hint',
          'internalNote',
          'image',
          'lenderGroupId',
          'location',
          'maxBooking',
          'preparationTime',
          'reactionTime',
          'visibilityGroupIds',
          'targetUserIds',
          'editors',
          'productType',
          'products',
          'categories',
          'lender',
          'availabilityType',
          'possibleBookingSlots',
          'recurringIntervals',
          'availablePickUps',
          'maxPreBooking',
          'maxArticleAmount',
          'isAmountPossible',
          'isIndependentOfLenderTimes'
        ]),
        categoryIds: initialProduct.categories?.map(c => c.id),
        locationId: initialProduct.location?.id,
        possibleBookingSlots:
          initialProduct.possibleBookingSlots?.length === 0
            ? [{ start: new Date(), end: new Date() }]
            : initialProduct.possibleBookingSlots,
        recurringIntervals:
          initialProduct.recurringIntervals?.length === 0
            ? [{ weekdays: [], start: 480, end: 960 }]
            : initialProduct.recurringIntervals,
        internalNote: initialProduct.internalNote || ''
      }
    : {
        name: '',
        description: '',
        // categoryId: null,
        locationId: null,
        hint: '',
        internalNote: '',
        image: null,
        lenderGroupId: null,
        location: null,
        maxBooking: { value: 8, unit: Unit.HOUR },
        preparationTime: { value: 1, unit: Unit.HOUR },
        reactionTime: { value: '', unit: '' },
        visibilityGroupIds: [],
        targetUserIds: [],
        editors: [],
        productType: ProductType.BASIC,
        maxPreBooking: '',
        lender: {
          email: currentUser?.email || '',
          phone: ''
          // isBookingMailActive: false
        },
        availabilityType: AvailabilityType.ALWAYS,
        possibleBookingSlots: [{ start: new Date(), end: new Date() }],
        recurringIntervals: [{ weekdays: [], start: 480, end: 960 }],
        products: [],
        categoryIds: [],
        maxArticleAmount: 1,
        isAmountPossible: false,
        isIndependentOfLenderTimes: false
      };
};
